import { Box, Card, CardHeader, Typography } from '@mui/material';

import { OrderContext, Plan, PlanFamily, Subscription, TaxDetails } from '@localstack/types';

import { useMemo } from 'react';

import { ActionTitle, DecorativeDivider } from '../../../../display';
import { CounterInput } from '../../../../feedback';

import { PlansList } from '../../PlansList';

interface PlanSelectorProps {
  adminMode: boolean;
  tax: Optional<TaxDetails>;
  sortedPlans: Plan[];
  plan: Plan;
  handleChangePlan: (plan: Plan) => void;
  context: OrderContext;
  seats: number;
  setSeats: React.Dispatch<React.SetStateAction<number>>;
  subscription: Optional<Subscription>;
}

export const PlanSelector = ({
  adminMode,
  tax,
  sortedPlans,
  plan,
  handleChangePlan,
  context,
  seats,
  setSeats,
  subscription,
}: PlanSelectorProps): JSX.Element => {
  const sortedPlansWithID = useMemo(
    () =>
      sortedPlans.map((p) => ({
        ...p,
        name: `${p.name} (${p.id})`,
      })),
    [sortedPlans],
  );

  return (
    <Card variant="outlined" sx={{ padding: 0 }}>
      <CardHeader title="Select a Plan" />
      <PlansList
        tax={tax}
        plans={adminMode ? sortedPlansWithID : sortedPlans}
        selected={plan}
        subscription={subscription}
        onSelect={handleChangePlan}
        context={context}
        isAdminMode={!!adminMode}
      />
      <DecorativeDivider />
      <Box p={2}>
        <ActionTitle
          title={
            <Typography>
              <label id="seats-label">
                Seats
                {subscription && (
                  <Typography component="span" variant="body2" color="text.secondary">
                    {` (currently ${subscription.seats})`}
                  </Typography>
                )}
              </label>
            </Typography>
          }
          actions={
            <CounterInput
              value={seats}
              onSetValue={(value) => setSeats(Math.max(1, value))}
              onIncrement={() => setSeats((s) => s + 1)}
              onDecrement={() => (seats > 1 ? setSeats((s) => s - 1) : null)}
              max={adminMode ? undefined : (plan.plan_seat_limit ?? Infinity)}
              disabled={plan.family === PlanFamily.HOBBY_PLANS}
              ariaLabelledBy="seats-label"
            />
          }
        />
      </Box>
    </Card>
  );
};
